import { useForm, useFormState } from "react-hook-form";
import { Member } from "src/redux/types/_customer.type";
import {
  EditMemberFormValues,
  EditMemberSchema,
} from "../schema/edit-member.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEditMember } from "../hooks/useEditMember";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  FormLabel,
} from "src/components/form/form";
import { Input } from "../../supplements/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";
import { Button } from "src/components/buttons/shadcn-button";
import { MapPin, Phone, User } from "lucide-react";
import { StateList } from "src/utils/states";

function SectionHeader({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title?: string;
  subtitle?: string;
}) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-0">{title}</h3>
        {subtitle && (
          <p className="text-xs text-slate-400 mt-0.5 mb-0">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

function RequiredLabel({ children }: { children: React.ReactNode }) {
  return (
    <FormLabel className="text-slate-600 text-sm font-medium mb-0">
      {children} <span className="text-red-400">*</span>
    </FormLabel>
  );
}

const fieldClass =
  "h-9 w-full px-3 rounded-xl font-normal border border-[#307bc4] bg-slate-50/50 focus-visible:bg-white focus-visible:ring-1 focus-visible:ring-[#274760] focus-visible:border-[#307bc4] transition-colors";

const EditMemberForm = ({ member }: { member: Member }) => {
  const form = useForm<EditMemberFormValues>({
    resolver: zodResolver(EditMemberSchema),
    defaultValues: member,
  });

  const { dirtyFields, isDirty } = useFormState({ control: form.control });

  const { mutate: editMemberMutation, isPending } = useEditMember();

  const saveChanges = () => {
    const values = form.getValues();

    const changedValues = Object.keys(dirtyFields).reduce((acc, key) => {
      acc[key as keyof EditMemberFormValues] =
        values[key as keyof EditMemberFormValues];
      return acc;
    }, {} as Partial<EditMemberFormValues>);

    if (Object.keys(changedValues).length === 0) {
      toast.info("No changes to save");
      return;
    }

    editMemberMutation(
      {
        memberId: member.id,
        values: changedValues,
        subscriptionId: member.subscription_id,
      },
      {
        onSuccess: () => {
          form.reset(form.getValues());
        },
      },
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="w-[80%] mx-auto shadow-md rounded-lg"
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(saveChanges)}
          className="px-2 lg:px-7 pb-7 pt-4"
        >
          <div className="flex flex-col gap-8">
            {/* Personal details */}
            <section>
              <SectionHeader
                icon={<User size={17} />}
                title="Personal Details"
                subtitle="As it appears on their ID"
              />
              <div className="grid md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel>First Name</RequiredLabel>
                      <FormControl>
                        <Input
                          placeholder="Fist Name"
                          {...field}
                          className={fieldClass}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="middle_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-slate-600 text-sm font-medium mb-0">
                        Middle Name
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Middle Name"
                          {...field}
                          className={fieldClass}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel>Last Name</RequiredLabel>
                      <FormControl>
                        <Input
                          placeholder="Last Name"
                          {...field}
                          className={fieldClass}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel>Gender</RequiredLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className={fieldClass}>
                            <SelectValue placeholder="Select Gender" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {["Male", "Female", "Other"].map((gender) => (
                            <SelectItem key={gender} value={gender}>
                              {gender}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>

            <div className="h-px bg-slate-100" />

            {/* Contact details */}
            <section>
              <SectionHeader
                icon={<Phone size={17} />}
                title="Contact Details"
                subtitle="We'll use these to send updates and reminders"
              />
              <div className="grid md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="phone_no"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel>Contact Number</RequiredLabel>
                      <FormControl>
                        <Input
                          placeholder="10-digit mobile number"
                          {...field}
                          className={fieldClass}
                          maxLength={10}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="whatsapp_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-slate-600 text-sm font-medium mb-0">
                        WhatsApp Number
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Same as contact number? Leave blank"
                          {...field}
                          className={fieldClass}
                          maxLength={10}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mt-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel>Email</RequiredLabel>
                      <FormControl>
                        <Input
                          placeholder="example@gmail.com"
                          type="email"
                          {...field}
                          className={fieldClass}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>

            <div className="h-px bg-slate-100" />

            {/* Address */}
            <section>
              <SectionHeader
                icon={<MapPin size={17} />}
                title="Address"
                subtitle="Used for home visits and delivery, if applicable"
              />
              <div className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel>Address</RequiredLabel>
                      <FormControl>
                        <Input
                          placeholder="House / Street / Locality"
                          {...field}
                          className={fieldClass}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel>City / Town</RequiredLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter your City"
                            {...field}
                            className={fieldClass}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="pin"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel>Pincode</RequiredLabel>
                        <FormControl>
                          <Input
                            placeholder="6-digit pincode"
                            {...field}
                            className={fieldClass}
                            maxLength={6}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="state"
                    defaultValue={undefined}
                    render={({ field: stateField }) => (
                      <FormItem>
                        <RequiredLabel>State</RequiredLabel>
                        <Select
                          onValueChange={(value) => {
                            stateField.onChange(value);
                            form.setValue("district", "");
                          }}
                          value={stateField.value}
                        >
                          <FormControl>
                            <SelectTrigger className={fieldClass}>
                              <SelectValue placeholder="Select State" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {StateList.map((state) => (
                              <SelectItem key={state.state} value={state.state}>
                                {state.state}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="district"
                    defaultValue={undefined}
                    render={({ field: districtField }) => (
                      <FormItem>
                        <RequiredLabel>District</RequiredLabel>
                        <Select
                          onValueChange={districtField.onChange}
                          value={districtField.value}
                          disabled={!form.getValues("state")}
                        >
                          <FormControl>
                            <SelectTrigger className={fieldClass}>
                              <SelectValue
                                placeholder={
                                  form.getValues("state")
                                    ? "Select District"
                                    : "Select a state first"
                                }
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {form.getValues("state") &&
                              StateList.find(
                                (item) =>
                                  item.state === form.getValues("state"),
                              )?.districts.map((district, index) => (
                                <SelectItem key={index} value={district}>
                                  {district}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </section>
          </div>

          {/* Submit */}
          <div className="flex justify-center mt-9 pt-6 border-t border-slate-100">
            <Button
              type="submit"
              className="h-11 px-6 rounded-lg bg-blue-600 hover:bg-blue-700 gap-2 min-w-[160px]"
              disabled={!isDirty || isPending}
            >
              {isPending ? (
                <span className="loading loading-dots loading-md"></span>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </motion.div>
  );
};

export default EditMemberForm;
